import { Alert, Platform, Share } from 'react-native';
import type { TripTemplate } from '@/types/tripTemplates';
import { buildTemplateShareUrl } from '@/navigation/templateDeepLink';
import { copyToClipboard } from '@/utils/clipboard';
import { logAnalyticsEvent } from '@/services/analyticsLogger';
import { isTemplateTopicTag } from '@/utils/templateTags';

type TemplateShareSource = 'template_card' | 'template_detail' | 'post_use_prompt' | 'unknown';
type TemplateShareTarget = Pick<TripTemplate, 'id' | 'title'> &
  Partial<Pick<TripTemplate, 'tags' | 'travelStyle'>>;

const pickTopicTag = (template: TemplateShareTarget, preferredTopicTag?: string): string | undefined => {
  if (preferredTopicTag) return preferredTopicTag;
  const travelStyleKeys = new Set((template.travelStyle ?? []).map((style) => String(style).toLowerCase()));
  return (template.tags ?? []).find((tag) => isTemplateTopicTag(tag, travelStyleKeys));
};

export async function shareTemplate(
  template: TemplateShareTarget,
  opts: {
    source?: TemplateShareSource;
    topicTag?: string;
    onCopied?: () => void;
    onShared?: () => void;
  } = {},
): Promise<void> {
  const topicTag = pickTopicTag(template, opts.topicTag);
  const url = buildTemplateShareUrl({
    templateId: template.id,
    topic: topicTag,
    source: opts.source ?? 'unknown',
  });
  const message = `Plan ${template.title} in Tripvio: ${url}`;

  try {
    if (Platform.OS === 'web') {
      await copyToClipboard(url);
      opts.onCopied?.();
    } else {
      await Share.share({
        title: template.title,
        message,
        url,
      });
      opts.onShared?.();
    }

    void logAnalyticsEvent('template_shared', {
      templateId: template.id,
      templateTitle: template.title,
      topicTag,
      source: opts.source ?? 'unknown',
      platform: Platform.OS,
    });
  } catch (error) {
    console.warn('[TemplateShare] Failed to share template', error);
    Alert.alert('Share failed', 'Unable to share this template right now.');
  }
}
